/**
 * Data layer for `platform-fee-bank`. Thin typed fetch wrappers over /api/v1/platform-fee-bank.
 * Raw `fetch()` is confined to this file per the dash contract.
 */

export interface PlatformFeeBankRow {
	id: string;
	tenant_id: string;
	created_at: number;
	updated_at: number;
	[key: string]: unknown;
}

export interface ListPlatformFeeBankResponse {
	count: number;
	items: PlatformFeeBankRow[];
}

export interface CreatePlatformFeeBankBody {
	[key: string]: unknown;
}

const BASE = "/api/v1/platform-fee-bank";

async function parseOrThrow<T>(res: Response): Promise<T> {
	const text = await res.text();
	let data: unknown = null;
	if (text) {
		try {
			data = JSON.parse(text);
		} catch {
			data = text;
		}
	}
	if (!res.ok) {
		const msg =
			data &&
			typeof data === "object" &&
			data !== null &&
			"error" in data &&
			typeof (data as { error: unknown }).error === "string"
				? (data as { error: string }).error
				: `HTTP ${res.status}`;
		throw new Error(msg);
	}
	return data as T;
}

export async function list(): Promise<ListPlatformFeeBankResponse> {
	const res = await fetch(BASE, {
		method: "GET",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<ListPlatformFeeBankResponse>(res);
}

export async function create(body: CreatePlatformFeeBankBody): Promise<PlatformFeeBankRow> {
	const res = await fetch(BASE, {
		method: "POST",
		credentials: "include",
		headers: {
			accept: "application/json",
			"content-type": "application/json",
		},
		body: JSON.stringify(body ?? {}),
	});
	return parseOrThrow<PlatformFeeBankRow>(res);
}

export async function remove(id: string): Promise<{ removed: boolean }> {
	const res = await fetch(`${BASE}/${encodeURIComponent(id)}`, {
		method: "DELETE",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<{ removed: boolean }>(res);
}

export const platformFeeBankQueryKeys = {
	all: ["platform-fee-bank"] as const,
	list: () => [...platformFeeBankQueryKeys.all] as const,
	detail: (id: string) => [...platformFeeBankQueryKeys.all, id] as const,
};
